import { ChevronRight, FileText, ScrollText, Search, Settings, KeyRound, Briefcase, Globe2, ExternalLink, Sparkles, Shield, FileSignature, Smartphone, TrendingUp, Camera, Mail, Users, Database, GitBranch, UserCheck, Calendar, BarChart3, BookOpen, CreditCard, Receipt, MapPin, MessageCircle, GraduationCap, Calculator } from "lucide-react";
import { Link } from "react-router-dom";
import { useSuperMode } from "../../context/SuperModeContext";

// V27 — set of items always visible in Normal Mode. Everything else is
// gated behind Super Mode (MPIN unlock).
const NORMAL_MODE_TAB_IDS = new Set([
    "stats", "conversions", "unified-inbox", "inquiries",
    "registrations", "tutor-applications", "app-leads",
    "payment-submissions", "payment-links", "fee-reminders", "fee-recommendation", "invoices",
    "unresolved-cities", "profile",
    // V156/V157 — post-tutor-acceptance workflow (Classroom & Assignments).
    "assignments",
]);

// V21 Phase 1 — Mobile shortcut cards for the most-used CRM workflows. Renders
// as 2-up large tappable tiles at the top of Manage, ahead of the rest.
// V141 — Invoices promoted to a Quick Access tile so admins can complete the
// full "create invoice → share on WhatsApp" workflow from mobile in two taps.
const PRIMARY_SHORTCUTS = [
    { id: "payment-links", icon: CreditCard, label: "Payments", desc: "Create · share · mark paid", color: "text-emerald-700 bg-emerald-100" },
    { id: "invoices", icon: Receipt, label: "Invoices", desc: "Create · share on WhatsApp", color: "text-indigo-700 bg-indigo-100" },
    { id: "registrations", icon: GitBranch, label: "Demo Leads", desc: "Manage all leads", color: "text-orange-700 bg-orange-100" },
    { id: "tutor-applications", icon: UserCheck, label: "Tutors", desc: "Approve · assign", color: "text-purple-700 bg-purple-100" },
    { id: "payment-submissions", icon: FileSignature, label: "Payments Received", desc: "Verify · reconcile submissions", color: "text-teal-700 bg-teal-100" },
    { id: "assignments", icon: GraduationCap, label: "Classroom & Assignments", desc: "Classes · Attendance · Tasks", color: "text-blue-800 bg-sky-100" },
    { id: "master-data", icon: Database, label: "Master Data", desc: "Subjects · grades · cities", color: "text-blue-700 bg-blue-100" },
];

const SECTIONS = [
    {
        title: "Content",
        items: [
            { id: "blogs", icon: FileText, label: "Blog Posts", desc: "Write & publish new articles", color: "text-amber-600 bg-amber-50" },
            { id: "policies", icon: ScrollText, label: "Policies", desc: "Edit Terms, Privacy, Tutor & Parent policies", color: "text-rose-600 bg-rose-50" },
            { id: "seo", icon: Search, label: "SEO Meta", desc: "Page-level title & description overrides", color: "text-blue-600 bg-blue-50" },
            { id: "seo-insights", icon: TrendingUp, label: "SEO Insights", desc: "GA4 + Search Console — keywords, top pages, conversions", color: "text-emerald-600 bg-emerald-50" },
            { id: "team-management", icon: Users, label: "Team Management", desc: "Founder + team: CRUD · photos · drag-reorder · publish/draft", color: "text-purple-700 bg-purple-50" },
            { id: "tutor-email-templates", icon: Mail, label: "Tutor Email Templates", desc: "Multi-template manager for Become-a-Tutor confirmation email", color: "text-blue-700 bg-blue-50" },
            { id: "ai-marketing", icon: Sparkles, label: "Marketing Templates", desc: "Ready-to-use blog titles, ad copy & social posts", color: "text-amber-600 bg-amber-50" },
        ],
    },
    {
        title: "Operations",
        items: [
            { id: "unified-inbox", icon: Mail, label: "Unified Inbox", desc: "Every lead in one feed · triage fast", color: "text-blue-700 bg-blue-50" },
            { id: "registrations", icon: GitBranch, label: "Demo Registrations", desc: "Every demo request · full submission details", color: "text-orange-700 bg-orange-50" },
            { id: "inquiries", icon: Mail, label: "Parent Inquiries", desc: "Contact form submissions · reply on WhatsApp", color: "text-amber-700 bg-amber-50" },
            { id: "tutor-applications", icon: UserCheck, label: "Tutor Applications", desc: "Approve · assign · view Aadhaar", color: "text-purple-700 bg-purple-50" },
            { id: "assignments", icon: BookOpen, label: "Classroom & Assignments", desc: "Post-acceptance · portal links · sessions · attendance", color: "text-cyan-700 bg-cyan-50" },
            { id: "invoices", icon: Receipt, label: "Invoices", desc: "Create · edit · share via WhatsApp · mark paid", color: "text-indigo-700 bg-indigo-50" },
            { id: "payment-links", icon: CreditCard, label: "Payment Links", desc: "Create amount-locked links · share on WhatsApp · mark paid", color: "text-emerald-700 bg-emerald-50" },
            { id: "fee-reminders", icon: MessageCircle, label: "Fee Reminders", desc: "Search parent · official template · open WhatsApp", color: "text-green-700 bg-green-50" },
            { id: "fee-recommendation", icon: Calculator, label: "Fee Recommendation", desc: "City tier · approved range · budget check · quotation", color: "text-indigo-700 bg-indigo-50" },
            { id: "payment-submissions", icon: FileSignature, label: "Payment Submissions", desc: "Verify · reconcile parent-uploaded proofs", color: "text-teal-700 bg-teal-50" },
            { id: "payment-consents", icon: FileSignature, label: "Payment Consents", desc: "Immutable audit log of T&C acceptances", color: "text-teal-600 bg-teal-50" },
            { id: "app-leads", icon: Smartphone, label: "App Leads", desc: "Phone numbers from Book My Tutor download form", color: "text-cyan-600 bg-cyan-50" },
        ],
    },
    {
        title: "System",
        items: [
            { id: "settings", icon: Settings, label: "Site Settings", desc: "Analytics · WhatsApp · Resend · Socials", color: "text-slate-700 bg-slate-100" },
            { id: "unresolved-cities", icon: MapPin, label: "Unresolved Cities", desc: "Foreign + typo cities missing from the state map", color: "text-rose-700 bg-rose-50" },
            { id: "photo-management", icon: Camera, label: "Photo Management", desc: "Founder photo — upload, replace, hide, audit log", color: "text-pink-700 bg-pink-50" },
            { id: "login-activity", icon: Shield, label: "Login Activity", desc: "Security audit log of admin sign-ins", color: "text-indigo-600 bg-indigo-50" },
            { id: "profile", icon: KeyRound, label: "My Profile", desc: "Account info & change password", color: "text-emerald-600 bg-emerald-50" },
        ],
    },
];

const QUICK_LINKS = [
    { to: "/", label: "View Public Site", icon: Globe2 },
    { to: "/blog", label: "Browse Blog", icon: FileText },
];

export default function MobileManage({ onNavigate, liveClassCount = 0 }) {
    const sm = useSuperMode();
    const visibleShortcuts = sm.active ? PRIMARY_SHORTCUTS
        : PRIMARY_SHORTCUTS.filter((s) => NORMAL_MODE_TAB_IDS.has(s.id));
    const visibleSections = SECTIONS.map((sec) => ({
        ...sec,
        items: sm.active ? sec.items : sec.items.filter((i) => NORMAL_MODE_TAB_IDS.has(i.id)),
    })).filter((sec) => sec.items.length > 0);
    return (
        <div data-testid="mobile-manage" className="px-4 py-4 space-y-5 pb-4">
            {/* V21 Phase 1 — Primary shortcut tiles (2-up large tappable cards) */}
            <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold mb-2 px-1">Quick Access</div>
                <div className="grid grid-cols-2 auto-rows-fr gap-3" data-testid="mobile-primary-shortcuts">
                    {visibleShortcuts.map((s) => (
                        <button
                            key={s.id}
                            onClick={() => onNavigate(s.id)}
                            data-testid={`mobile-shortcut-${s.id}`}
                            className="bg-white border border-slate-200 rounded-2xl p-4 text-left active:scale-95 transition-transform"
                        >
                            <div className="flex items-start justify-between mb-2">
                                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${s.color}`}>
                                    <s.icon className="w-5.5 h-5.5" />
                                </div>
                                {s.id === "assignments" && liveClassCount > 0 && (
                                    <span
                                        data-testid="mobile-shortcut-live-badge"
                                        aria-label={`${liveClassCount} class${liveClassCount === 1 ? "" : "es"} live now`}
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-rose-500 text-white"
                                    >
                                        <span className="relative flex h-1.5 w-1.5" aria-hidden>
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-white" />
                                            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                                        </span>
                                        {liveClassCount} LIVE
                                    </span>
                                )}
                            </div>
                            <div className="font-heading font-bold text-[#0F172A] text-sm">{s.label}</div>
                            <div className="text-[11px] text-slate-500 truncate">{s.desc}</div>
                        </button>
                    ))}
                </div>
            </div>
            {visibleSections.map((sec) => (
                <div key={sec.title}>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold mb-2 px-1">{sec.title}</div>
                    <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden">
                        {sec.items.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => onNavigate(item.id)}
                                data-testid={`mobile-manage-${item.id}`}
                                className="w-full flex items-center gap-3 p-4 text-left active:bg-slate-50 hover:bg-slate-50 transition-colors"
                            >
                                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${item.color} shrink-0`}>
                                    <item.icon className="w-5 h-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-heading font-semibold text-[#0F172A] text-sm inline-flex items-center gap-1.5">
                                        {item.label}
                                        {item.id === "assignments" && liveClassCount > 0 && (
                                            <span
                                                data-testid="mobile-manage-live-badge"
                                                aria-label={`${liveClassCount} class${liveClassCount === 1 ? "" : "es"} live now`}
                                                className="relative inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-rose-500 text-white"
                                            >
                                                <span className="relative flex h-1.5 w-1.5" aria-hidden>
                                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-white" />
                                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white" />
                                                </span>
                                                {liveClassCount} LIVE
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-slate-500 truncate">{item.desc}</div>
                                </div>
                                <ChevronRight className="w-4 h-4 text-slate-300" />
                            </button>
                        ))}
                    </div>
                </div>
            ))}

            <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-bold mb-2 px-1">Quick Links</div>
                <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden">
                    {QUICK_LINKS.map((q) => (
                        <Link
                            key={q.to}
                            to={q.to}
                            target="_blank"
                            className="w-full flex items-center gap-3 p-4 text-left active:bg-slate-50 hover:bg-slate-50 transition-colors"
                        >
                            <div className="w-10 h-10 rounded-xl bg-blue-50 text-[#1E3A8A] flex items-center justify-center shrink-0">
                                <q.icon className="w-5 h-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-heading font-semibold text-[#0F172A] text-sm">{q.label}</div>
                                <div className="text-xs text-slate-500 inline-flex items-center gap-1">Opens in new tab <ExternalLink className="w-3 h-3" /></div>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>

            <div className="text-center text-[10px] text-slate-400 pt-4">
                Anand Tutorials Admin · v1.0 · {new Date().getFullYear()}
            </div>
        </div>
    );
}
